'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useRouter } from '@/i18n/navigation'
import { X, Phone, ShieldCheck, RefreshCw } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import OtpInputs from './OtpInputs'
import OtpTimer from './OtpTimer'
import OtpErrorMessage from './OtpErrorMessage'
import { getClientLocale } from '@/lib/client-locale'

import { getApiUrl } from '@/lib/api-url'
import { normalizeDigits } from '@hamishon/shared-utils'

export type OtpAuthSource = 'homepage' | 'order' | 'protected' | 'generic' | 'assistant'
export type OtpRole = 'CUSTOMER' | 'INSTALLER'

export interface OtpAuthContextState {
  source: OtpAuthSource
  redirectTo?: string
  role?: OtpRole
}

interface OtpAuthModalProps {
  isOpen: boolean
  /** Closed by the visitor — X or backdrop — without logging in. */
  onClose: () => void
  /**
   * Closed because the login went through. Kept apart from `onClose` so a
   * caller that reacts to a dismissal (by navigating the guest away, say)
   * does not also react to a success.
   */
  onAuthenticated?: () => void
  context: OtpAuthContextState
}

type OtpStep = 'role' | 'phone' | 'otp'

export default function OtpAuthModal({
  isOpen,
  onClose,
  onAuthenticated,
  context,
}: OtpAuthModalProps) {
  const t = useTranslations('auth')
  const router = useRouter()
  const { loginWithOtp } = useAuth()
  const [step, setStep] = useState<OtpStep>('phone')
  const [role, setRole] = useState<OtpRole>('CUSTOMER')
  const [mobileNumber, setMobileNumber] = useState('')
  const [otpValue, setOtpValue] = useState('')
  const [expiresAt, setExpiresAt] = useState<Date | null>(null)
  const [resendEnabled, setResendEnabled] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [showLockout, setShowLockout] = useState(false)
  const [lockoutMessage, setLockoutMessage] = useState('')
  const [changeMode, setChangeMode] = useState(false)
  const [pendingNumber, setPendingNumber] = useState('')
  const autoVerifyRef = useRef<string | null>(null)
  const language = getClientLocale()

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const shouldShowRole = context.source === 'homepage' && !context.role
    setStep(shouldShowRole ? 'role' : 'phone')
    setRole(context.role || 'CUSTOMER')
    setMobileNumber('')
    setOtpValue('')
    setExpiresAt(null)
    setResendEnabled(false)
    setError(null)
    setLoading(false)
    setShowLockout(false)
    setLockoutMessage('')
    setChangeMode(false)
    setPendingNumber('')
    autoVerifyRef.current = null
  }, [isOpen, context])

  const isMobileValid = useMemo(() => /^09\d{9}$/.test(mobileNumber), [mobileNumber])
  const isPendingMobileValid = useMemo(() => /^09\d{9}$/.test(pendingNumber), [pendingNumber])

  if (!isOpen) {
    return null
  }

  const handleClose = () => {
    if (!loading) {
      onClose()
    }
  }

  const handleSendOtp = async (targetNumber: string) => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${getApiUrl()}/auth/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(language ? { 'x-language': language } : {}),
        },
        body: JSON.stringify({ mobileNumber: targetNumber, userType: role }),
      })

      const data = await response.json()
      if (!response.ok) {
        if (data?.requiresCaptcha) {
          setLockoutMessage(data.message || t('otp.verificationRequired'))
          setShowLockout(true)
          return
        }
        throw new Error(data.message || t('otp.sendFailed'))
      }

      setExpiresAt(new Date(data.expiresAt))
      setResendEnabled(false)
      setStep('otp')
    } catch (err: any) {
      setError(err.message || t('otp.sendFailed'))
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOtp = async (source: 'auto' | 'manual') => {
    if (otpValue.length !== 6 || loading) return
    if (source === 'auto' && autoVerifyRef.current === otpValue) return

    setLoading(true)
    setError(null)
    autoVerifyRef.current = otpValue

    try {
      await loginWithOtp(mobileNumber, otpValue, role)
      const closeAfterSuccess = onAuthenticated ?? onClose
      closeAfterSuccess()

      const defaultRedirectBySource: Record<OtpAuthSource, string> = {
        assistant: '/assistant',
        homepage: '/dashboard',
        order: '/dashboard',
        protected: '/dashboard',
        generic: '/dashboard',
      }
      const redirectTarget = context.redirectTo || defaultRedirectBySource[context.source]
      router.push(redirectTarget)
    } catch (err: any) {
      setError(err.message || t('otp.codeIncorrect'))
    } finally {
      setLoading(false)
    }
  }

  const handleOtpChange = (nextValue: string) => {
    setOtpValue(nextValue)
    if (nextValue.length === 6) {
      handleVerifyOtp('auto')
    }
  }

  const handleResend = async () => {
    if (!resendEnabled || loading) return
    setOtpValue('')
    autoVerifyRef.current = null
    setError(null)
    await handleSendOtp(mobileNumber)
  }

  const handleChangeNumber = async () => {
    if (!isPendingMobileValid) {
      setError(t('otp.mobileNotValid'))
      return
    }
    if (pendingNumber === mobileNumber) {
      setChangeMode(false)
      setError(null)
      return
    }
    setMobileNumber(pendingNumber)
    setOtpValue('')
    setChangeMode(false)
    await handleSendOtp(pendingNumber)
  }

  const renderRoleStep = () => (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-title-lg font-semibold text-foreground">{t('otp.roleTitle')}</h2>
        <p className="text-body-md text-muted-foreground">{t('otp.roleSubtitle')}</p>
      </div>
      <div className="grid grid-cols-1 gap-3">
        <button
          onClick={() => {
            setRole('CUSTOMER')
            setStep('phone')
          }}
          className="w-full rounded-lg border border-outline px-4 py-3 text-body-lg font-medium text-foreground hover:bg-surface-variant transition"
        >
          {t('otp.roleCustomer')}
        </button>
        <button
          onClick={() => {
            setRole('INSTALLER')
            setStep('phone')
          }}
          className="w-full rounded-lg border border-outline px-4 py-3 text-body-lg font-medium text-foreground hover:bg-surface-variant transition"
        >
          {t('otp.roleInstaller')}
        </button>
      </div>
    </div>
  )

  const renderPhoneStep = () => (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-title-lg font-semibold text-foreground">{t('otp.smsTitle')}</h2>
        <p className="text-body-md text-muted-foreground">
          {t('otp.smsSubtitle')}
        </p>
      </div>

      {error && <OtpErrorMessage message={error} />}

      <div className="space-y-2">
        <label className="text-body-md font-medium text-foreground">{t('otp.mobileLabel')}</label>
        <div className="relative">
          <Phone size={18} className="absolute start-3 top-3 text-muted-foreground" />
          <input
            type="tel"
            inputMode="numeric"
            value={mobileNumber}
            onChange={(event) =>
              setMobileNumber(
                normalizeDigits(event.target.value).replace(/\D/g, '').slice(0, 11),
              )
            }
            onKeyDown={(event) => {
              if (event.key === 'Enter' && isMobileValid && !loading) {
                event.preventDefault()
                void handleSendOtp(mobileNumber)
              }
            }}
            placeholder="09xxxxxxxxx"
            className="w-full rounded-lg border border-outline bg-surface py-3 ps-10 pe-3 text-body-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        {!isMobileValid && mobileNumber.length > 0 && (
          <p className="text-label-sm text-destructive">{t('otp.mobileInvalid')}</p>
        )}
      </div>

      <button
        onClick={() => handleSendOtp(mobileNumber)}
        disabled={!isMobileValid || loading}
        className="w-full rounded-lg bg-primary py-3 text-body-md font-semibold text-primary-foreground hover:bg-primary-hover transition disabled:opacity-50"
      >
        {loading ? t('otp.sending') : t('otp.confirm')}
      </button>
    </div>
  )

  const renderOtpStep = () => (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-title-lg font-semibold text-foreground">{t('otp.codeTitle')}</h2>
        <p className="text-body-md text-muted-foreground">
          {t('otp.codeSentTo', { mobile: mobileNumber })}
        </p>
      </div>

      {error && <OtpErrorMessage message={error} />}

      <OtpInputs value={otpValue} onChange={handleOtpChange} autoFocus />

      <div className="flex items-center justify-between text-body-md">
        <span className="text-muted-foreground">{t('otp.timeLeft')}</span>
        <OtpTimer
          expiresAt={expiresAt}
          onExpire={() => {
            setResendEnabled(true)
          }}
        />
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={() => setChangeMode((prev) => !prev)}
          className="text-body-sm text-primary hover:text-primary-hover"
        >
          {t('otp.changeNumber')}
        </button>
        <button
          onClick={handleResend}
          disabled={!resendEnabled || loading}
          className="flex items-center gap-2 text-body-sm text-foreground disabled:text-muted-foreground"
        >
          <RefreshCw size={14} />
          {t('otp.resend')}
        </button>
      </div>

      {changeMode && (
        <div className="rounded-lg border border-outline p-3 space-y-3">
          <label className="text-body-md font-medium text-foreground">{t('otp.newNumber')}</label>
          <input
            type="tel"
            inputMode="numeric"
            value={pendingNumber}
            onChange={(event) =>
              setPendingNumber(
                normalizeDigits(event.target.value).replace(/\D/g, '').slice(0, 11),
              )
            }
            placeholder="09xxxxxxxxx"
            className="w-full rounded-lg border border-outline bg-surface py-2 px-3 text-body-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
          {!isPendingMobileValid && pendingNumber.length > 0 && (
            <p className="text-label-sm text-destructive">{t('otp.mobileInvalid')}</p>
          )}
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => setChangeMode(false)}
              className="text-body-sm text-muted-foreground hover:text-foreground"
            >
              {t('otp.cancel')}
            </button>
            <button
              onClick={handleChangeNumber}
              disabled={!isPendingMobileValid || loading}
              className="rounded-lg bg-primary px-4 py-2 text-body-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              {t('otp.confirmNumber')}
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => handleVerifyOtp('manual')}
        disabled={otpValue.length !== 6 || loading}
        className="w-full rounded-lg bg-primary py-3 text-body-md font-semibold text-primary-foreground hover:bg-primary-hover transition disabled:opacity-50"
      >
        {loading ? t('otp.verifying') : t('otp.confirm')}
      </button>
    </div>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative w-full max-w-md rounded-2xl bg-surface p-6 shadow-level-2">
        <button
          onClick={handleClose}
          className="absolute end-4 top-4 rounded-full p-2 text-muted-foreground hover:text-foreground"
        >
          <X size={18} />
        </button>

        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-container text-primary">
            <ShieldCheck size={20} />
          </div>
          <div>
            <p className="text-title-md font-semibold text-foreground">{t('otp.modalTitle')}</p>
            <p className="text-body-sm text-muted-foreground">{t('otp.modalSubtitle')}</p>
          </div>
        </div>

        {step === 'role' && renderRoleStep()}
        {step === 'phone' && renderPhoneStep()}
        {step === 'otp' && renderOtpStep()}
      </div>

      {showLockout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative w-full max-w-sm rounded-2xl bg-surface p-6 shadow-level-2 space-y-4">
            <h3 className="text-title-md font-semibold text-foreground">{t('otp.lockoutTitle')}</h3>
            <p className="text-body-md text-muted-foreground">
              {lockoutMessage || t('otp.lockoutDefault')}
            </p>
            <button
              onClick={() => setShowLockout(false)}
              className="w-full rounded-lg bg-primary py-2 text-body-md font-semibold text-primary-foreground"
            >
              {t('otp.lockoutAck')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
